import React, {
    useState, useEffect, useCallback, useMemo, useRef,
    createContext, useContext
} from 'react'
import { useIntl } from 'react-intl'
import { get } from 'api'
import PubSub from 'pubsub-js'
import paths from 'app/paths'
import Fuse from 'fuse.js'
import debounce from 'lodash/debounce'
import { useNavigate, useLocation, matchPath } from 'react-router-dom'
import { useOrganization } from 'contexts/organization'
import { useMe } from 'contexts/me'
import { useServiceOnboarding } from 'contexts/service-onboarding'
import { useBillingBannerMessage } from 'app/layouts/private/banner/billing'
import { useStorage } from 'hooks/storage'
import { getUserAgent } from 'utilities/user-agent'
import { getPeopleProfileUrl, getPeopleCompanyUrl, getPeopleTeamUrl, getPeopleLocationUrl } from 'utilities/url'
import { getFullName } from 'utilities/person'
import { omit } from 'utilities/object'
import { move, compact } from 'utilities/array'
import {
    checkIsQueryValid, checkIsSavable,
    getStringOrRegexMatcher,
    useGetItemLabel
} from 'app/layouts/private/command-bar/utilities'
import { useAddEntityCommands, useFixedCommands, useGroupCommands, useProfileCommands } from 'app/layouts/private/command-bar/commands'
import { categoryToI18nMap as deviationCategoryToI18nMap } from 'pages/deviation/constants'
import { typeToI18nMap as absenceTypeToI18nMap } from 'pages/absence/constants'
import { AlertTriangleFilled as Warning } from 'components/feather'

// Actions icons
import {
    CornerDownLeft as Select,
    XCircle as Dismiss,
    Copy,
    Star as Favorite,
    ArrowUp, ArrowDown
} from 'styled-icons/feather'
import { Unstar as Unfavorite } from 'components/feather'

const CommandBarContext = createContext()

const CommandBarProvider = ({ children }) => {
    const { formatMessage } = useIntl()
    const getItemLabel = useGetItemLabel()

    const navigate = useNavigate()
    const location = useLocation()

    const { os: { isMacOS } } = getUserAgent()

    const { organization } = useOrganization()
    const { isItMyOwnId } = useMe()

    const {
        announcements,
        newBadgeItems
    } = useServiceOnboarding()

    /**
     * GENERAL
     */
    const containerRef = useRef()
    const inputRef = useRef()
    const actionsInputRef = useRef()

    /**
     * MODAL
     */
    const [show, setShow] = useState(false)
    const snapStateRef = useRef(SNAP_DEFAULT_STATE)
    const [containerPosition, setContainerPosition] = useState(SNAP_DEFAULT_POSITION)
    const [isDragging, setIsDragging] = useState(false)
    const dragOffset = useRef({ x: 0, y: 0 })
    const [snapState, setSnapState] = useState(SNAP_DEFAULT_STATE)

    useEffect(() => {
        PubSub.subscribe('command-bar.toggle', () => {
            setShow(show => !show)
        })

        return () => PubSub.unsubscribe('command-bar.toggle')
    }, [])

    useEffect(() => {
        snapStateRef.current = snapState
    }, [snapState])

    const onMouseDown = useCallback(e => {
        const { clientX, clientY } = e
        const { x, y } = containerPosition

        setIsDragging(true)

        dragOffset.current = {
            x: clientX - x,
            y: clientY - y
        }

        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
    }, [containerPosition])

    const onMouseMove = useCallback(e => {
        const newXRaw = e.clientX - dragOffset.current.x
        const newYRaw = e.clientY - dragOffset.current.y

        let newX = newXRaw
        let newY = newYRaw

        let newSnapState = {
            left: { snapped: false, snappable: false },
            right: { snapped: false, snappable: false },
            top: { snapped: false, snappable: false }
        }

        // Left
        if(newXRaw >= 0 && newXRaw <= SNAP_THRESHOLD) {
            if(snapStateRef.current.left.snappable) {
                newX = 0
                newSnapState.left = { snapped: true, snappable: true }
            } else {
                newSnapState.left = { snapped: false, snappable: true }
            }
        }

        // Right
        if(newXRaw < 0 && Math.abs(newXRaw) <= SNAP_THRESHOLD) {
            if(snapStateRef.current.right.snappable) {
                newX = 0
                newSnapState.right = { snapped: true, snappable: true }
            } else {
                newSnapState.right = { snapped: false, snappable: true }
            }
        }

        // Top
        if(newYRaw >= 0 && newYRaw <= SNAP_THRESHOLD) {
            if(snapStateRef.current.top.snappable) {
                newY = 0
                newSnapState.top = { snapped: true, snappable: true }

            } else {
                newSnapState.top = { snapped: false, snappable: true }
            }
        }

        setSnapState(newSnapState)
        setContainerPosition({ x: newX, y: newY })
    }, [])

    const onMouseUp = useCallback(() => {
        inputRef.current?.focus?.()
        setIsDragging(false)

        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
    }, [onMouseMove, inputRef])

    const resetContainerPosition = () => {
        setContainerPosition(SNAP_DEFAULT_POSITION)
        setSnapState(SNAP_DEFAULT_STATE)
    }

    /**
     * SEARCH
     */
    const [query, setQuery] = useState('')
    const [isSearching, setIsSearching] = useState(false)
    const [searchResults, setSearchResults] = useState([])
    const resultsCacheRef = useRef({}) // Cache for search results to avoid redundant API calls
    const lastQueryRef = useRef('') // Track the last query. Used for better filtering
    const isQueryValid = checkIsQueryValid(query)

    const fetchSearchResults = async search => {
        const { ok, response } = await get({
            path: '/units',
            params: {
                search,
                includeDeactivated: true,
                types: ['user', 'group']
            }
        })

        let results = []

        if(ok && !!response.items?.length) {
            results = response.items.map(({ type, ...entity }) => ({
                actionId: `${type}:${entity.id}`,
                type: 'entity',
                entityType: type,
                entity: {
                    ...entity,
                    ...(type === 'user' ? { name: getFullName(entity) } : null)
                }
            }))
        }

        return { ok, response: results }
    }

    // Debounced search to reduce API calls
    const debouncedSearch = useMemo(() => debounce(async search => {
        if(!resultsCacheRef.current[search]) {
            const { response } = await fetchSearchResults(search)
            resultsCacheRef.current[search] = response

            setIsSearching(false)

            setSearchResults(response)
        }
    }, 300), [])

    // Handle search input
    const handleSearch = async e => {
        const { value } = e.currentTarget
        const previousQuery = lastQueryRef.current

        setQuery(value)

        // Reset search results if query is invalid
        if(!checkIsQueryValid(value)) {
            setSearchResults([])
            debouncedSearch.cancel()
            setIsSearching(false)

            // Reset the last query
            lastQueryRef.current = ''

            return
        }

        setIsSearching(true)

        // Use cached results if available
        if(resultsCacheRef.current[value]) {
            setSearchResults(resultsCacheRef.current[value])
            debouncedSearch.cancel()
            setIsSearching(false)
            lastQueryRef.current = value
            return
        }

        // If we have existing results and the new query is a refinement of the previous query
        // (e.g., "hum" -> "huma"), filter the existing results to only show those that still match
        if(previousQuery && value.startsWith(previousQuery) && !!searchResults.length) {
            // Filter function to match the current query
            const filterByCurrentQuery = item => {
                // Only applicable to entities (search results)
                if(!item?.entity) {
                    return false
                }

                const label = getItemLabel(item)

                return label.toLowerCase().includes(value.toLowerCase())
            }

            const filteredResults = searchResults.filter(filterByCurrentQuery)

            setSearchResults(filteredResults)
        }

        // Update the last query reference
        lastQueryRef.current = value

        // Perform the actual search
        debouncedSearch(value)
    }

    /**
     * ANNOUNCEMENTS
     */
    const billing = useBillingBannerMessage()

    const [announcement] = announcements.filter(({ meta }) => !!meta.banner && !!meta.current && !meta.dismissed)

    /**
     * COMMANDS & NAVIGATION
     */
    const checkForStringOrRegexMatch = getStringOrRegexMatcher(query)

    const [activeIndex, setActiveIndex] = useState(0)

    const [favorites, setFavorites] = useStorage({
        key: `command-bar:${organization?.id}:favorites`,
        defaultValue: []
    })
    const [recentActions, setRecentActions] = useStorage({
        key: `command-bar:${organization?.id}:actions`,
        defaultValue: []
    })
    const [dismissedNewlyAdded, setDismissedNewlyAdded] = useStorage({
        key: 'dismissed:newly-added',
        defaultValue: []
    })

    // If the query changes, reset the active index
    useEffect(() => setActiveIndex(0), [query])

    const clearFavorites = useCallback(() => {
        setFavorites([])
        inputRef.current?.focus()
    }, [setFavorites])
    const clearRecentActions = useCallback(() => {
        setRecentActions([])
        inputRef.current?.focus()
    }, [setRecentActions])

    const checkIsFavorite = item => {
        if(!item?.actionId) {
            return false
        }

        return !!favorites.find(favorite => favorite.actionId === item.actionId)
    }
    const checkIsRecent = item => {
        if(!item?.actionId) {
            return false
        }

        return !!recentActions.find(recent => recent.actionId === item.actionId)
    }

    const addEntityCommands = useAddEntityCommands({ query })
    const fixedCommands = useFixedCommands({
        query,
        container: {
            position: containerPosition,
            resetPosition: resetContainerPosition
        },
        favorites, clearFavorites, recentActions, clearRecentActions
    })
    const profileCommands = useProfileCommands()
    const groupCommands = useGroupCommands()

    const enrichCommand = useCallback(item => {
        let { onClick, link, icon } = item

        // Get reference from favorites/recent actions
        const reference = fixedCommands.find(({ actionId }) => actionId === item.actionId)

        if(!onClick && !link?.to && !link?.href) {
            if(reference) {
                onClick = reference.onClick
                link = reference.link
            }
        }

        if(!icon && reference?.icon) {
            icon = reference.icon
        }

        return {
            ...item,
            onClick,
            link,
            icon
        }
    }, [fixedCommands])

    // Get the valid favorites (those that have a corresponding fixed command)
    const validFavorites = useMemo(() => {
        return favorites
            .map(enrichCommand)
            .filter(favorite => {
                if(favorite.type !== 'fixed') {
                    return true
                }

                if(favorite.subLabel) {
                    // Find the corresponding fixed command, where the subLabel.id isn't part of the actionId
                    // E.g. `action:registerAbsence:vacation` ➜ `action:registerAbsence`
                    const match = favorite.actionId.split(':').slice(0, -1).join(':')
                    const fixedCommand = fixedCommands.find(({ actionId }) => actionId === match)

                    return !!fixedCommand
                }

                // Find the corresponding fixed command
                const fixedCommand = fixedCommands.find(({ actionId }) => actionId === favorite.actionId)

                return !!fixedCommand
            })
    }, [favorites, fixedCommands, enrichCommand])

    const navigationItems = useMemo(() => {
        if(!isQueryValid) {
            return {
                ...((billing || announcement) ? {
                    announcements: {
                        total: (billing && announcement ? 2 : 1),
                        items: compact([
                            billing && {
                                ...billing,
                                actionId: 'billing',
                                icon: Warning,
                                heading: formatMessage({
                                    id: 'payment_heading_payment_issue',
                                    defaultMessage: 'Payment issue'
                                }),
                                type: 'announcement',
                                link: { to: paths.settings.subscription.base },
                                ignoreRecent: true,
                                savable: false,
                                className: billing.type
                            },
                            announcement && {
                                ...announcement,
                                actionId: announcement.id,
                                type: 'announcement',
                                link: { href: announcement.url },
                                external: true,
                                ignoreRecent: true,
                                savable: false,
                                className: announcement.type
                            }
                        ])
                    }
                } : {}),
                favorites: {
                    total: validFavorites.length,
                    reset: clearFavorites,
                    items: validFavorites.map(enrichCommand)
                },
                recentActions: {
                    total: recentActions.length,
                    reset: clearRecentActions,
                    items: recentActions.map(enrichCommand)
                }
            }
        }

        const combined = [
            ...fixedCommands?.reduce((accumulator, command) => {
                let {
                    matches = [],
                    displayMatch = false
                } = command

                let subLabels = matches.filter(checkForStringOrRegexMatch)

                if(!!subLabels.length && displayMatch) {
                    subLabels = subLabels.map(subLabel => ({
                        ...command,
                        actionId: `${command.actionId}:${subLabel.id}`,
                        subLabel,
                        // Append the subLabel's variant (like absence type or deviation category) to the link state
                        ...(command?.link ?
                            {
                                link: {
                                    ...command.link,
                                    state: {
                                        ...command.link.state,
                                        [subLabel.variant]: subLabel.id
                                    }
                                }
                            }
                            : null
                        )
                    }))
                }

                return [
                    ...accumulator,
                    omit(command, 'displayMatch'),
                    ...subLabels
                ]
            }, []) ?? [],
            ...searchResults
        ]

        const fuse = new Fuse(combined, {
            keys: [
                { name: 'subLabel.text', weight: 1 },
                {
                    name: 'label',
                    weight: 0.9,
                    getFn: obj => {
                        const label = getItemLabel(obj)

                        return label
                    }
                },
                { name: 'acronym', weight: 0.7 },
                {
                    name: 'matches',
                    weight: 0.3,
                    getFn: obj => {
                        // Convert matches array to searchable string
                        if(Array.isArray(obj?.matches)) {
                            return obj.matches.map(match => {
                                if(match?.text) {
                                    return match.text
                                }

                                if(match instanceof RegExp) {
                                    return match.test(query) ? query : ''
                                }

                                return match
                            }).join(' ')
                        }

                        return ''
                    }
                }
            ],
            threshold: 0.4,
            // ignoreLocation: true
        })

        const fuseResults = fuse.search(query)

        return {
            commands: {
                total: fuseResults.length,
                items: fuseResults.map(({ item }) => item)
            },
            suggestions: {
                total: addEntityCommands.length,
                items: addEntityCommands
            }
        }
    }, [query, isQueryValid, favorites, recentActions, searchResults, fixedCommands, enrichCommand, addEntityCommands, clearFavorites, clearRecentActions, billing, announcement, checkForStringOrRegexMatch])

    const combinedItems = Object.values(navigationItems).reduce((accumulator, { items }) => {
        return [
            ...accumulator,
            ...items
        ]
    }, [])
    const activeItem = combinedItems[activeIndex]

    const updateFavorites = item => {
        if(!item) {
            return
        }

        if(typeof item?.icon !== 'string') {
            delete item.icon
        }

        const previousFavorites = [...favorites]
        const previousRecentActions = [...recentActions]

        // If already a favorite, remove it
        const existingFavoritesIndex = previousFavorites.findIndex(({ actionId }) => actionId === item.actionId)

        if(existingFavoritesIndex > -1) {
            previousFavorites.splice(existingFavoritesIndex, 1)

            // If at main search, move the active index
            if(!isQueryValid) {
                const newGlobalIndex = existingFavoritesIndex !== previousFavorites.length ?
                    existingFavoritesIndex
                    : existingFavoritesIndex - 1

                setActiveIndex(newGlobalIndex)
            }
        } else {
            // Remove the oldest favorite if the limit is reached
            if(previousFavorites.length >= MAX_FAVORITES) {
                previousFavorites.pop()
            }

            // Add the new favorite
            previousFavorites.unshift(item)
        }

        setFavorites(previousFavorites)

        // If in recent actions, and not a favorite, remove it
        const existingRecentIndex = previousRecentActions.findIndex(({ actionId }) => actionId === item.actionId)

        if(existingRecentIndex > -1 && existingFavoritesIndex === -1) {
            previousRecentActions.splice(existingRecentIndex, 1)

            setRecentActions(previousRecentActions)
        }
    }

    const moveFavorite = (item, direction) => {
        if(!item || !checkIsFavorite(item) || favorites.length < 2 || !direction) {
            return
        }

        const previousFavorites = [...validFavorites]
        const currentIndex = previousFavorites.findIndex(({ actionId }) => actionId === item.actionId)

        if(currentIndex === -1) {
            return
        }

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1

        if(newIndex < 0 || newIndex >= previousFavorites.length) {
            return
        }

        const updatedFavorites = move(previousFavorites, currentIndex, newIndex)
        setFavorites(updatedFavorites)

        const newGlobalIndex = combinedItems.findIndex(({ actionId }) => actionId === previousFavorites[newIndex].actionId)
        setActiveIndex(newGlobalIndex)
    }

    const updateRecentActions = item => {
        if(!item) {
            return
        }

        if(typeof item?.icon !== 'string') {
            delete item.icon
        }

        const previousActions = [...recentActions]

        // If already a recent action, move it to the top
        const existingIndex = previousActions.findIndex(({ actionId }) => actionId === item.actionId)

        if(existingIndex > -1) {
            setRecentActions(previousActions => move(previousActions, existingIndex, 0))

            return
        }

        // Remove the oldest recent action if the limit is reached
        if(previousActions.length >= MAX_RECENT) {
            previousActions.pop()
        }

        const updatedActions = [
            item,
            ...previousActions ?? []
        ]

        // Add the new recent action
        setRecentActions(updatedActions)
    }

    const execute = item => {
        if(!item) {
            return
        }

        let {
            onClick,
            dismiss,
            link = {},
            external = false,
            ignoreRecent = false,
            executeOptions = {}
        } = item

        const { shouldCloseCommandBar = true } = executeOptions

        // Update recent actions, unless explicitly ignored or from favorites
        if(!ignoreRecent && !checkIsFavorite(item)) {
            updateRecentActions(item)
        }

        shouldCloseCommandBar && setShow(false)
        reset(executeOptions)
        onClick?.(item)

        if(newBadgeItems?.[item?.category]?.actionIds?.includes(item?.actionId)) {
            setTimeout(() => setDismissedNewlyAdded([
                ...dismissedNewlyAdded,
                `module:${item?.category}`
            ]), 1500)
        }
        // Match the current route
        const match = !!link?.to ?
            matchPath(link.to, location.pathname)
            : null

        // Entity navigation
        const entityNavigateTo = {
            user: getPeopleProfileUrl,
            company: getPeopleCompanyUrl,
            team: getPeopleTeamUrl,
            location: getPeopleLocationUrl
        }[item?.entityType] ?? null

        // Execute the item
        if(!!entityNavigateTo && item?.entity) {
            navigate(entityNavigateTo(item?.entity))
        } else if(link?.href) {
            global.open(link.href, !!external ? '_blank' : '_self')
        } else if(!!link?.to) {
            navigate(link.to, {
                state: {
                    ...link.state,
                    explicit: true
                },
                replace: !!match
            })
        }

        if(item.toast) {
            const activeItem = combinedItems[activeIndex]
            const label = getItemLabel(activeItem)

            showToast({
                message: item.toast,
                ...(label ? { label } : null)
            })
        }

        dismiss?.()
    }

    const reset = useCallback(({ shouldResetSearch = true, shouldCloseActions = true } = {}) => {
        // Search
        if(shouldResetSearch) {
            setQuery('')
            setSearchResults([])
            setActiveIndex(0)
        }

        // Actions
        if(shouldCloseActions) {
            setIsShowingActions(false)
        }
        setActionsQuery('')
        setActiveActionsIndex(0)
    }, [])

    const cyclePrevious = () => {
        // Cycle through the items
        if(!isShowingActions) {
            if(activeIndex === 0) {
                setActiveIndex(combinedItems.length - 1)
            } else {
                setActiveIndex(activeIndex => activeIndex - 1)
            }
        // Cycle through the actions
        } else {
            if(activeActionsIndex === 0) {
                setActiveActionsIndex(actionableActionsItems.length - 1)
            } else {
                setActiveActionsIndex(activeActionsIndex => activeActionsIndex - 1)
            }
        }
    }

    const cycleNext = () => {
        // Cycle through the items
        if(!isShowingActions) {
            if(activeIndex === combinedItems.length - 1) {
                setActiveIndex(0)
            } else {
                setActiveIndex(activeIndex => activeIndex + 1)
            }
        // Cycle through the actions
        } else {
            if(activeActionsIndex === actionableActionsItems.length - 1) {
                setActiveActionsIndex(0)
            } else {
                setActiveActionsIndex(activeActionsIndex => activeActionsIndex + 1)
            }
        }
    }

    /**
     * ACTIONS
     */
    const [actionsQuery, setActionsQuery] = useState('')
    const [isShowingActions, setIsShowingActions] = useState(false)
    const [activeActionsIndex, setActiveActionsIndex] = useState(0)
    const [toast, setToast] = useState(null)
    const toastTimeoutRef = useRef()

    // If the modal closes, close the actions
    useEffect(() => {
        if(!show) {
            setIsShowingActions(false)
        }
    }, [show])

    // If the actionsQuery changes, reset the active actions index
    useEffect(() => setActiveActionsIndex(0), [actionsQuery])

    const actionsItems = useMemo(() => compact([
        {
            actionId: 'select',
            icon: Select,
            label: formatMessage({
                id: 'action_select',
                defaultMessage: 'Select'
            }),
            ignoreRecent: true,
            savable: false,
            onClick: () => execute(activeItem)
        },

        // Dismiss announcement
        (activeItem?.type === 'announcement' && activeItem.dismiss) && {
            actionId: 'announcement:dismiss',
            icon: Dismiss,
            label: formatMessage({
                id: 'action_dismiss',
                defaultMessage: 'Dismiss'
            }),
            ignoreRecent: true,
            savable: false,
            executeOptions: { shouldCloseCommandBar: false },
            onClick: () => {
                activeItem.dismiss()
                setActiveIndex(0)
            }
        },

        // Profile commands
        ...((activeItem?.entityType === 'user' && !isItMyOwnId(activeItem?.entity?.id)) ?
            profileCommands
                .map(command => ({
                    ...command(activeItem.entity),
                    ignoreRecent: true,
                    savable: false
                }))
                .filter(({ requirements = [] }) => requirements.every(Boolean))
            : []),
        // Group commands
        ...(['team', 'location'].includes(activeItem?.entityType) ?
            groupCommands
                .map(command => ({
                    ...command(activeItem),
                    ignoreRecent: true,
                    savable: false
                }))
                .filter(({ requirements = [] }) => requirements.every(Boolean))
            : []),

        // Report deviation categories
        ...((activeItem?.actionId === 'action:reportDeviation') ?
            Object.entries(deviationCategoryToI18nMap).map(([key, id]) => ({
                actionId: `deviation:category:${key}`,
                category: 'deviation',
                label: formatMessage({ id }),
                ignoreRecent: true,
                savable: false
            }))
            : []),
        // Register absence types
        ...((activeItem?.actionId === 'action:registerAbsence') ?
            Object.entries(absenceTypeToI18nMap).map(([key, id]) => ({
                actionId: `absence:type:${key}`,
                category: 'absence',
                label: formatMessage({ id }),
                ignoreRecent: true,
                savable: false
            }))
            : []),
        // Copy name
        (activeItem?.type === 'entity' && global.navigator?.clipboard) && {
            actionId: 'entity:copy-name',
            icon: Copy,
            label: formatMessage({
                id: 'command_bar_action_copy_name',
                defaultMessage: 'Copy name'
            }),
            toast: formatMessage({
                id: 'command_bar_toast_copied_name',
                defaultMessage: 'Name copied to clipboard'
            }),
            ignoreRecent: true,
            savable: false,
            executeOptions: {
                shouldCloseCommandBar: false,
                shouldResetSearch: false
            },
            onClick: () => global.navigator.clipboard.writeText(getItemLabel(activeItem))
        },

        // Divider
        { type: 'divider' },

        // Move favorite up
        (
            checkIsFavorite(activeItem) &&
            favorites.length > 1 &&
            favorites?.[0]?.actionId !== activeItem?.actionId
        ) && {
            actionId: 'favorite:move-up',
            icon: ArrowUp,
            label: formatMessage({
                id: 'command_bar_action_move_favorite_up',
                defaultMessage: 'Move up in Favorites'
            }),
            toast: formatMessage({
                id: 'command_bar_toast_moved_favorite_up',
                defaultMessage: 'Moved up in Favorites'
            }),
            ignoreRecent: true,
            savable: false,
            executeOptions: {
                shouldCloseCommandBar: false,
                shouldCloseActions: false
            },
            shortcut: ['mod', 'shift', 'arrowup'],
            onClick: () => moveFavorite(activeItem, 'up')
        },
        // Move favorite down
        (
            checkIsFavorite(activeItem) &&
            favorites.length > 1 &&
            favorites?.[favorites.length - 1]?.actionId !== activeItem?.actionId
        ) && {
            actionId: 'favorite:move-down',
            icon: ArrowDown,
            label: formatMessage({
                id: 'command_bar_action_move_favorite_down',
                defaultMessage: 'Move down in Favorites'
            }),
            toast: formatMessage({
                id: 'command_bar_toast_moved_favorite_down',
                defaultMessage: 'Moved down in Favorites'
            }),
            ignoreRecent: true,
            savable: false,
            executeOptions: {
                shouldCloseCommandBar: false,
                shouldCloseActions: false
            },
            shortcut: ['mod', 'shift', 'arrowdown'],
            onClick: () => moveFavorite(activeItem, 'down')
        },
        // Favorite
        checkIsSavable(activeItem) && {
            actionId: `favorite:${checkIsFavorite(activeItem) ? 'remove' : 'add'}`,
            icon: checkIsFavorite(activeItem) ? Unfavorite : Favorite,
            label: formatMessage({
                id: checkIsFavorite(activeItem) ?
                    'command_bar_action_remove_from_favorites'
                    : 'command_bar_action_add_to_favorites',
                defaultMessage: checkIsFavorite(activeItem) ?
                    'Remove from Favorites'
                    : 'Add to Favorites'
            }),
            toast: formatMessage({
                id: checkIsFavorite(activeItem) ?
                    'command_bar_toast_removed_from_favorites'
                    : 'command_bar_toast_added_to_favorites',
                defaultMessage: checkIsFavorite(activeItem) ?
                    'Removed from Favorites'
                    : 'Added to Favorites'
            }),
            ignoreRecent: true,
            savable: false,
            executeOptions: { shouldCloseCommandBar: false },
            shortcut: ['mod', 'shift', 'f'],
            onClick: () => updateFavorites(activeItem)
        },

        // Remove from recent actions
        checkIsRecent(activeItem) && {
            actionId: 'recent:remove',
            icon: Dismiss,
            label: formatMessage({
                id: 'command_bar_action_remove_from_recent',
                defaultMessage: 'Remove from Recent actions'
            }),
            toast: formatMessage({
                id: 'command_bar_toast_removed_from_recent',
                defaultMessage: 'Removed from Recent actions'
            }),
            ignoreRecent: true,
            savable: false,
            executeOptions: { shouldCloseCommandBar: false },
            onClick: () => {
                const previousActions = [...recentActions]
                const existingIndex = previousActions.findIndex(({ actionId }) => actionId === activeItem.actionId)

                if(existingIndex > -1) {
                    previousActions.splice(existingIndex, 1)
                    setRecentActions(previousActions)
                }
            }
        }
    ]), [
        activeItem,
        updateFavorites, moveFavorite, checkIsFavorite, favorites,
        recentActions, checkIsRecent,
        profileCommands, groupCommands
    ])
    const actionableActionsItems = actionsItems.filter(action => {
        if(action.type === 'divider') {
            return false
        }

        if(actionsQuery) {
            return action.label.toLowerCase().includes(actionsQuery.toLowerCase())
        }

        return true
    })
    const activeActionItem = actionableActionsItems[activeActionsIndex]

    // Reset active actions index when toggling actions popover
    useEffect(() => {
        if(!isShowingActions) {
            setActiveActionsIndex(0)
        }
    }, [isShowingActions])

    const showToast = useCallback(message => {
        if(toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current)
        }

        setToast(message)

        toastTimeoutRef.current = setTimeout(() => {
            setToast(null)
            toastTimeoutRef.current = null
        }, TOAST_TIMEOUT)
    }, [])

    /**
     * KEYBOARD SHORTCUTS
     * - Cmd/Ctrl + Shift + F: Add/remove from favorites
     * - Cmd/Ctrl + .: Toggle actions popover
     * - Arrow Up/Down: Cycle through items
     * - Enter: Execute the active item/action
     * - Escape: Close the modal/actions popover
     */
    const onKeyDown = e => {
        let { key, metaKey, ctrlKey, shiftKey } = e
        key = key.toLowerCase()
        const modifierPressed = (!!metaKey && !!isMacOS) || (!!ctrlKey && !isMacOS)

        // Prevent default behavior for navigation keys
        if(NAVIGATION_KEYS.includes(key)) {
            if((key === 'tab' && show) || key !== 'tab') {
                e.preventDefault()
            }
        }

        // Execute the active item
        if(key === 'enter') {
            if(!isShowingActions && activeItem) {
                execute(activeItem)
            } else if(isShowingActions && activeActionItem) {
                execute(activeActionItem)
            }
        }

        // Toggle actions popover (Cmd/Ctrl + .)
        if(modifierPressed && key === '.') {
            if(!!combinedItems.length && actionableActionsItems.length > 1) {
                setIsShowingActions(isShowingActions => !isShowingActions)
            }
        }

        if(key === 'arrowup') {
            cyclePrevious()
        }

        if(key === 'arrowdown') {
            cycleNext()
        }

        if(key === 'escape') {
            if(isShowingActions) {
                setIsShowingActions(false)
            } else {
                setShow(false)
                reset()
            }
        }

        // Add/remove from favorites (Cmd/Ctrl + Shift + F)
        if(modifierPressed && shiftKey && key === 'f') {
            e.preventDefault()

            if(!checkIsSavable(activeItem)) {
                return
            }

            const toastLabel = compact([
                getItemLabel(activeItem),
                activeItem?.subLabel?.text && `(${activeItem.subLabel.text})`
            ]).join(' ')

            showToast({
                message: formatMessage({
                    id: checkIsFavorite(activeItem) ?
                        'command_bar_toast_removed_from_favorites'
                        : 'command_bar_toast_added_to_favorites',
                    defaultMessage: checkIsFavorite(activeItem) ?
                        'Removed from Favorites'
                        : 'Added to Favorites'
                }),
                label: toastLabel
            })

            updateFavorites(activeItem)
        }

        // Move favorite up (Cmd/Ctrl + Shift + Arrow Up)
        if(modifierPressed && shiftKey && key === 'arrowup') {
            e.preventDefault()

            if(
                !checkIsFavorite(activeItem) ||
                favorites.length < 2 ||
                favorites?.[0]?.actionId === activeItem?.actionId
            ) {
                return
            }

            showToast({
                message: formatMessage({
                    id: 'command_bar_toast_moved_favorite_up',
                    defaultMessage: 'Moved up in Favorites'
                }),
                label: getItemLabel(activeItem)
            })

            moveFavorite(activeItem, 'up')
        }

        // Move favorite down (Cmd/Ctrl + Shift + Arrow Down)
        if(modifierPressed && shiftKey && key === 'arrowdown') {
            e.preventDefault()

            if(
                !checkIsFavorite(activeItem) ||
                favorites.length < 2 ||
                favorites?.[favorites.length - 1]?.actionId === activeItem?.actionId
            ) {
                return
            }

            showToast({
                message: formatMessage({
                    id: 'command_bar_toast_moved_favorite_down',
                    defaultMessage: 'Moved down in Favorites'
                }),
                label: getItemLabel(activeItem)
            })

            moveFavorite(activeItem, 'down')
        }

        if(key !== 'escape') {
            if(!isShowingActions) {
                inputRef.current?.focus()
            } else {
                actionsInputRef.current?.focus()
            }
        }
    }

    const value = {
        // General
        containerRef,
        inputRef, actionsInputRef,

        // Modal
        show, setShow,

        // Drag & Snap
        onMouseDown,
        containerPosition,
        isDragging,
        resetContainerPosition,

        // Search
        query, setQuery, isQueryValid,
        fetchSearchResults, isSearching, setIsSearching,
        searchResults, handleSearch,

        // Announcement
        announcement,

        // Commands
        navigationItems, combinedItems,
        execute, onKeyDown, activeIndex, activeItem,
        checkIsFavorite,

        // Actions
        isShowingActions, setIsShowingActions,
        actionsQuery, setActionsQuery,
        activeActionsIndex,
        actionsItems, actionableActionsItems,
        toast
    }

    return (
        <CommandBarContext.Provider value={value}>
            {children}
        </CommandBarContext.Provider>
    )
}

const SNAP_DEFAULT_POSITION = { x: 0, y: 0 }
const SNAP_THRESHOLD = 20
const SNAP_DEFAULT_STATE = {
    left: { snapped: false, snappable: false },
    right: { snapped: false, snappable: false },
    top: { snapped: false, snappable: false }
}

const MAX_RECENT = 5
const MAX_FAVORITES = 10

const NAVIGATION_KEYS = [
    'arrowup',
    'arrowdown',
    'enter',
    'tab',
    'escape'
]

const TOAST_TIMEOUT = 5000

export const useCommandBar = () => {
    const context = useContext(CommandBarContext)

    if(!context) {
        throw new Error('useCommandBar must be used within a CommandBarProvider')
    }

    return context
}

export default CommandBarProvider